export type PreferenceKind = "boolean" | "string" | "number" | "json"

export type PreferenceDefinition<TKey extends string, TValue, TKind extends PreferenceKind> = {
  key: TKey
  kind: TKind
  defaultValue?: TValue
}

export const definePreference =
  <TValue>() =>
  <TKey extends string, TKind extends PreferenceKind>(
    definition: PreferenceDefinition<TKey, TValue, TKind>
  ) =>
    definition

export const buildPreferenceKeys = <
  TDefs extends Record<string, PreferenceDefinition<string, unknown, PreferenceKind>>,
>(
  definitions: TDefs
) =>
  Object.fromEntries(
    (Object.entries(definitions) as Array<[keyof TDefs, TDefs[keyof TDefs]]>).map(
      ([name, definition]) => [name, definition.key]
    )
  ) as {
    [K in keyof TDefs]: TDefs[K]["key"]
  }

export type PreferenceKey<
  TDefs extends Record<string, PreferenceDefinition<string, unknown, PreferenceKind>>,
> = TDefs[keyof TDefs]["key"]

export type PreferenceValueMap<
  TDefs extends Record<string, PreferenceDefinition<string, unknown, PreferenceKind>>,
> = {
  [K in keyof TDefs as TDefs[K]["key"]]: TDefs[K] extends PreferenceDefinition<
    string,
    infer TValue,
    PreferenceKind
  >
    ? TValue
    : never
}

/**
 * Extract preference names (object keys) filtered by kind.
 * Example: PreferenceNamesByKind<typeof userPreferenceDefinitions, "boolean">
 *          → "editLedger" | "expandedView" | ...
 */
export type PreferenceNamesByKind<
  TDefs extends Record<string, PreferenceDefinition<string, unknown, PreferenceKind>>,
  TKind extends PreferenceKind,
> = {
  [K in keyof TDefs]: TDefs[K]["kind"] extends TKind ? K : never
}[keyof TDefs]
